import React, { memo } from 'react';
import { makeStyles } from '@mui/styles';
import { Card, CardContent, CardActionArea, Typography, Avatar, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { getChartColors, withAlpha } from '../../../../utils/chartTheme';
import { dashboardCardSx } from '../card-style';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%'
  },
  header: {
    marginBottom: theme.spacing(1.5)
  },
  title: {
    fontWeight: 700
  },
  value: {
    fontWeight: 700,
    lineHeight: 1.15
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    display: 'block'
  },
  avatar: {
    height: 36,
    width: 36
  },
  sparkline: {
    height: 40,
    marginTop: theme.spacing(1.5)
  },
  difference: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center'
  },
  differenceIconSuccess: {
    color: 'green'
  },
  differenceIconError: {
    color: theme.palette.error.dark
  }
}));

const KpiCard = memo(function KpiCard(props) {
  const classes = useStyles();
  const { title, value, subtitle, comparation, positive, series, seriesKey, icon, tooltip, onClick, accentColor } = props;
  const colors = getChartColors();
  const accent = accentColor || colors.primary;

  const body = (
    <CardContent>
      <div className={classes.header} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon && (
          <Avatar
            className={`${classes.avatar} mexx-accent-avatar`}
            style={{
              '--mexx-accent-bg': withAlpha(accent, 0.14),
              '--mexx-accent-color': accent,
              flexShrink: 0
            }}
          >
            {icon}
          </Avatar>
        )}
        <Typography className={classes.title} color="textSecondary" variant="body2">
          {title}
        </Typography>
      </div>
      <Typography className={`${classes.value} mexx-dyn-color`} variant="h3" style={{ '--mexx-dyn-color': accent }}>{value}</Typography>
      {subtitle && (
        <Typography className={classes.subtitle} variant="caption" color="textSecondary">{subtitle}</Typography>
      )}
      {typeof comparation === 'number' && (
        <div className={classes.difference}>
          {positive ? (
            <ArrowUpwardIcon fontSize="small" className={classes.differenceIconSuccess} />
          ) : (
            <ArrowDownwardIcon fontSize="small" className={classes.differenceIconError} />
          )}
          <Typography variant="body2" className={positive ? classes.differenceIconSuccess : classes.differenceIconError}>
            {Math.abs(comparation)}%
          </Typography>
        </div>
      )}
      {series && series.length > 0 && (
        <div className={classes.sparkline}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <Line
                type="monotone"
                dataKey={seriesKey || 'count'}
                stroke={accent}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                style={{ filter: `drop-shadow(0 3px 4px ${withAlpha(accent, 0.65)})` }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </CardContent>
  );

  const card = (
    <Card className={classes.root} sx={dashboardCardSx}>
      {onClick ? (
        <CardActionArea onClick={onClick} style={{ height: '100%' }}>
          {body}
        </CardActionArea>
      ) : body}
    </Card>
  );

  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
});

export default KpiCard;
